import React, { useEffect, useState } from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Table, Tag, theme, Typography, Spin, Space } from 'antd';
import { Link } from 'react-router-dom';
import {
  AdminFoodLogDetailDto,
  FoodLogSource,
  FoodLogIngredientLineSource,
  ScanFeedback,
  CatalogSource,
} from '@wellverse/shared-ts';
import { Field } from '../../components/Field';
import { GpsLocation } from '../../components/GpsLocation';
import { axiosInstance, API_BASE_URL } from '../../providers/dataProvider';
import { hasGpsCoordinates } from '../../utils/map-url';

const { Text } = Typography;

const sourceColors: Record<FoodLogSource, string> = {
  [FoodLogSource.FOOD_SCAN]: '#3ecf8e',
  [FoodLogSource.FOOD_LABEL]: '#3ecf8e',
  [FoodLogSource.MANUAL]: '#898989',
  [FoodLogSource.BARCODE]: '#f5a623',
  [FoodLogSource.DESCRIBE_TEXT]: '#1890ff',
};

const feedbackColors: Record<ScanFeedback, string> = {
  [ScanFeedback.POSITIVE]: '#3ecf8e',
  [ScanFeedback.NEGATIVE]: '#ff6b6b',
};

const catalogSourceColors: Record<string, string> = {
  [CatalogSource.AI_GENERATED]: '#eb2f96',
  [CatalogSource.WELLCOME]: '#faad14',
  [CatalogSource.MANUAL]: '#1890ff',
  [CatalogSource.USDA]: '#52c41a',
  [CatalogSource.CFS]: '#13c2c2',
};

interface RelatedLlmLog {
  id: string;
  model: string;
  endpoint: string | null;
  status: string;
  latencyMs: number | null;
  createdAt: string;
  promptSlug: string | null;
}

interface RelatedLlmLogsResponse {
  visionJobId?: string;
  llmCallLogId?: string;
  items: RelatedLlmLog[];
}

export const FoodLogShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<AdminFoodLogDetailDto>({ resource: 'food-logs' });
  const item = queryResult?.data?.data;

  const [relatedLogs, setRelatedLogs] = useState<RelatedLlmLogsResponse | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    if (!item?.id) return;
    setLogsLoading(true);
    axiosInstance
      .get(`${API_BASE_URL}/food-admin/logs/${item.id}/related-llm-logs`)
      .then((res) => {
        const payload = res.data?.data ?? res.data;
        setRelatedLogs(payload ?? { items: [] });
      })
      .catch((err) => {
        console.error('Failed to load related LLM logs:', err);
        setRelatedLogs({ items: [] });
      })
      .finally(() => setLogsLoading(false));
  }, [item?.id]);

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  return (
    <Show resource="food-logs">
      <div style={{ padding: '8px 0' }}>
        <Field label="Food Name">{item.foodName}</Field>
        <Field label="User ID">{item.userId}</Field>
        <Field label="Source">
          <Tag
            style={{
              borderColor: sourceColors[item.source] || '#898989',
              color: sourceColors[item.source] || '#898989',
              background: `${sourceColors[item.source] || '#898989'}15`,
              borderRadius: 9999,
              padding: '2px 12px',
              fontWeight: 500,
            }}
          >
            {item.source}
          </Tag>
        </Field>
        <Field label="Amount">{item.amount} {item.amountUnit}</Field>
        <Field label="People Sharing">{item.peopleSharing}</Field>
        {item.mealImageUrl && (
          <Field label="Meal Image">
            <img
              src={item.mealImageUrl}
              alt="Meal"
              style={{ maxWidth: 300, borderRadius: 8 }}
            />
          </Field>
        )}
        {item.scannedLabel && <Field label="Scanned Label">{item.scannedLabel}</Field>}
        {item.nutrition && (
          <Field label="Nutrition">
            <div>
              {item.nutrition.calories} kcal · Protein {item.nutrition.proteinG}g · Fat {item.nutrition.fatG}g · Carbs {item.nutrition.carbG}g
              {item.nutrition.sodiumMg !== undefined && ` · Sodium ${item.nutrition.sodiumMg}mg`}
            </div>
            {item.nutrition.scoreSummary && <div>{item.nutrition.scoreSummary}</div>}
            {item.nutrition.suggestion && <div>{item.nutrition.suggestion}</div>}
          </Field>
        )}
        {item.scanFeedback && (
          <Field label="Scan Feedback">
            <Tag
              style={{
                borderColor: feedbackColors[item.scanFeedback] || '#898989',
                color: feedbackColors[item.scanFeedback] || '#898989',
                background: `${feedbackColors[item.scanFeedback] || '#898989'}15`,
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {item.scanFeedback}
            </Tag>
          </Field>
        )}
        {item.visualEntityId && (
          <Field label="Visual Entity">
            <Link to={`/visual-entities/show/${item.visualEntityId}`}>
              {item.visualEntityId}
            </Link>
          </Field>
        )}
        <Field label="Dish Catalog">
          {item.dishCatalogId ? (
            <Space>
              <Link to={`/dish-catalog/show/${item.dishCatalogId}`}>
                {item.dishCatalogName || item.dishCatalogId}
              </Link>
              {item.dishCatalogSource && (
                <Tag
                  style={{
                    borderColor: catalogSourceColors[item.dishCatalogSource] || '#898989',
                    color: catalogSourceColors[item.dishCatalogSource] || '#898989',
                    background: `${catalogSourceColors[item.dishCatalogSource] || '#898989'}15`,
                    borderRadius: 9999,
                    padding: '2px 12px',
                    fontWeight: 500,
                  }}
                >
                  {item.dishCatalogSource}
                </Tag>
              )}
            </Space>
          ) : (
            <Text type="secondary">No matched dish catalog (AI-only / unmatched scan)</Text>
          )}
        </Field>
        {(item.mealSetId || item.mealSetName) && (
          <Field label="Meal Set">{item.mealSetName ?? item.mealSetId}</Field>
        )}
        {(item.mealSetId || hasGpsCoordinates(item.mealSetLatitude, item.mealSetLongitude)) && (
          <Field label="Scan Location">
            <GpsLocation
              latitude={item.mealSetLatitude}
              longitude={item.mealSetLongitude}
              empty={<Text type="secondary">No GPS recorded for this meal set</Text>}
            />
          </Field>
        )}
        <Field label="Logged At">{new Date(item.loggedAt).toLocaleString()}</Field>
        <Field label="Created At">{new Date(item.createdAt).toLocaleString()}</Field>
        <Field label="Updated At">{new Date(item.updatedAt).toLocaleString()}</Field>
      </div>

      <h3 style={{ color: token.colorText, margin: '24px 0 16px' }}>Ingredients</h3>
      <Table
        dataSource={item.ingredients}
        rowKey="id"
        pagination={false}
        size="small"
      >
        <Table.Column
          dataIndex="displayName"
          title="Name"
          render={(name: string, record: { ingredientCatalogId?: string }) =>
            record.ingredientCatalogId ? (
              <Link to={`/ingredient-catalog/show/${record.ingredientCatalogId}`}>{name}</Link>
            ) : (
              name
            )
          }
        />
        <Table.Column
          dataIndex="amount"
          title="Amount"
          render={(_: number, record: any) => `${record.amount} ${record.amountUnit}`}
        />
        <Table.Column
          dataIndex="source"
          title="Source"
          render={(source: FoodLogIngredientLineSource) => (
            <Tag>{source}</Tag>
          )}
        />
        {item.ingredients.some((i) => i.confidence !== undefined) && (
          <Table.Column dataIndex="confidence" title="Confidence" render={(v: number) => v?.toFixed(2)} />
        )}
        <Table.Column dataIndex="nutritionSummaryLine" title="Nutrition Summary" />
      </Table>

      <h3 style={{ color: token.colorText, margin: '24px 0 8px' }}>Related LLM Logs</h3>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Best-effort link via visual entity metadata (`visionJobId` / `_llmCallLogId`).
        Historical rows often have no link until scan pipeline stores a job id.
      </Text>
      {logsLoading ? (
        <Spin />
      ) : relatedLogs && relatedLogs.items.length > 0 ? (
        <>
          {(relatedLogs.visionJobId || relatedLogs.llmCallLogId) && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              {relatedLogs.visionJobId && <>visionJobId: {relatedLogs.visionJobId} </>}
              {relatedLogs.llmCallLogId && <>· _llmCallLogId: {relatedLogs.llmCallLogId}</>}
            </Text>
          )}
          <Table dataSource={relatedLogs.items} rowKey="id" pagination={false} size="small">
            <Table.Column
              dataIndex="id"
              title="ID"
              render={(id: string) => (
                <Link to={`/llm-call-logs/show/${id}`}>{id.slice(0, 8)}…</Link>
              )}
            />
            <Table.Column dataIndex="model" title="Model" />
            <Table.Column dataIndex="promptSlug" title="Prompt" render={(v) => v || '—'} />
            <Table.Column dataIndex="endpoint" title="Endpoint" render={(v) => v || '—'} />
            <Table.Column
              dataIndex="status"
              title="Status"
              render={(status: string) => <Tag>{status}</Tag>}
            />
            <Table.Column
              dataIndex="latencyMs"
              title="Latency"
              render={(v: number | null) => (v != null ? `${v} ms` : '—')}
            />
            <Table.Column
              dataIndex="createdAt"
              title="Created"
              render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
            />
          </Table>
        </>
      ) : (
        <Text type="secondary">
          No related LLM call logs found
          {!item.visualEntityId
            ? ' (no visual entity on this log).'
            : ' (entity has no visionJobId / _llmCallLogId).'}
        </Text>
      )}
    </Show>
  );
};
